/* eslint-disable camelcase */
import querystring from 'querystring';

import { Button } from '@mui/material';
import mixpanel from 'mixpanel-browser';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import cookie from 'react-cookies';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
  withRouter,
} from 'react-router-dom';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';

import {
  isMixPanelEnabled,
  mixpanelReset,
  trackLogIn,
} from 'src/common/userTracking';
import { methodNameEnum } from 'src/recoil/selectors/makePHPRequest';
import { logoutAjax } from 'src/services/auth/logoutAjax';

import { selectRecoilUserSync } from '../app/store/recoilSyncSlice';
import Axios from '../common/axiosInterceptor';
import { IUserAtom, Role, user } from '../recoil/atoms/user';
import { permissionsSelector } from '../recoil/selectors/permissionsSelector';
import { ILoginAjaxReturn, loginAjax } from '../services/auth/loginAjax';
import { refreshTokenAjax } from '../services/auth/refreshTokenAjax';
import { getApiMutation } from '../services/rest/getApiMutation';
import { endpointEnum, makeAPIRequest } from '../services/rest/makeAPIRequest';

import BasePage from './BasePage';
import { bannedPasswordParts } from './components/bannedPasswordParts';
import { Layout } from './components/Layout';
import Login from './components/login/Login';
import { useSnackbarWithStyles } from './components/notifications/SnackbarProviderWithStyles';
import { VerifyEmail } from './modules/Adhoc/VerifyEmail';
import ErrorsPage from './modules/ErrorsExamples/ErrorsPage';
import { UnauthorizedPage } from './modules/Unauthorized/UnauthorizedPage';
import { AIOOutcome } from './pages/aio/AIOOutcome';
import ChangePassword from './pages/ChangePassword';
import { Leaderboard as OldLeaderboard } from './pages/Leaderboard';
import Leaderboard from './pages/leaderboard/Leaderboard';
import { OmniReporting } from './pages/OmniReporting';
// import { useDispatch } from 'react-redux';
// import { TLoginSettings, loginPut } from './store/loginSlice';
// import { IUserSettings } from '../recoil/selectors/permissionsSelector';
// import { AppDispatch } from './store';
// import { LAST_USER_ID } from '../constants';
// import { replaceUserSettings, saveAccessTokenJson } from './store/userSlice';

const ChangePasswordTyped = (ChangePassword as any) as React.FC<{
  handleLogin: (
    credentials: any,
    setStatus: any,
    setSubmitting: any,
    intl: any,
    resetInitialState: any
  ) => Promise<ILoginAjaxReturn>;
}>;
declare global {
  // eslint-disable-next-line no-unused-vars, autofix/no-unused-vars
  interface Window {
    kixieBuildEnv: {
      git: string;
    };
    Intercom: (
      string,
      options?: {
        email?: string;
        name?: string;
        phone?: string | null;
        user_id?: number;
      }
    ) => void;
  }
}

interface IBillingStatus {
  trialWithCard: boolean;
  paidPilot: boolean;
}

async function getBillingStatus(
  loginUser
): Promise<IBillingStatus | undefined> {
  if (!loginUser?.businessid || !loginUser?.apikey) {
    return;
  }
  const data = new FormData();
  data.append('businessid', (loginUser.businessid as unknown) as string);
  data.append('apikey', loginUser.apikey);
  data.append('call', methodNameEnum.GET_BILLING_STATUS);
  const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
  const response = await Axios.post(url, data, { withCredentials: true });
  return response.data?.result?.result;
}

async function tryTrackTrialStatus(loginUser) {
  if (!isMixPanelEnabled()) return;

  const billingStatus = await getBillingStatus(loginUser);
  if (!billingStatus) {
    return;
  }

  const trialStatus = {
    trialwithcard: billingStatus.trialWithCard,
    paidpilot: billingStatus.paidPilot,
  };
  try {
    mixpanel.register(trialStatus);
    mixpanel.people.set(trialStatus);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('mixpanel.register() failed', e);
  }
}

interface IRoutes extends RouteComponentProps {
  classes: string;
}
function Routes({ location: { pathname, search }, classes }: IRoutes) {
  const history = useHistory();
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [userAtomState, setUserAtom] = useRecoilState(user);
  const recoilUserSync = useSelector(selectRecoilUserSync);
  const userFromLocalStorage = localStorage.getItem('user');
  const userAtom =
    userAtomState && 'role' in userAtomState
      ? (userAtomState as IUserAtom)
      : userFromLocalStorage
      ? (JSON.parse(userFromLocalStorage) as IUserAtom)
      : undefined;
  const mutation = useMutation(
    getApiMutation(userAtom as IUserAtom, setUserAtom)
  );
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const permissions =
    'canViewCallReporting' in permissionsLoadable.contents
      ? permissionsLoadable.contents
      : undefined;

  // const dispatch = useDispatch<AppDispatch>();

  // const handleLoginSettings = (settings: TLoginSettings) => {
  //   dispatch(loginPut(settings));
  // };
  // const handleUserSettings = (settings: IUserSettings) => {
  //   const lastUserId = Number(localStorage.getItem(LAST_USER_ID));

  //   setCachedRefreshToken(settings.refreshToken);
  //   localStorage.setItem(LAST_USER_ID, String(settings.userid));
  //   dispatch(replaceUserSettings(settings));
  // };

  // const handleAccessTokenJson = (tokenJson: IAccessTokenJson) => {
  //   dispatch(saveAccessTokenJson(tokenJson));
  // };

  /**
   * Calculate the redirect path based on user role and saved redirect URL
   * @param {Object} loginUser - The logged in user object
   * @param {string} redirectUrl - The saved redirect URL from sessionStorage
   * @returns {string} The path to redirect to
   */
  const calculateRedirectPath = useCallback(
    (loginUser, redirectUrl) => {
      // Check for onboarding walkthrough first
      if (
        loginUser?.onboardingWalkthrough?.displayOnboarding &&
        process.env.VITE_APP_ONBOARDING === '1' &&
        (loginUser.user.role === Role.ADMIN ||
          loginUser.user.role === Role.MANAGER)
      ) {
        return '/onboarding-walkthrough';
      }

      // Check if saved redirect URL exists and is valid
      if (redirectUrl && redirectUrl !== '/') {
        return redirectUrl;
      }

      // Check for USER role with restricted reporting
      if (
        loginUser.user.role === Role.USER &&
        loginUser?.business?.onlyManagersCanViewCallReporting === 1
      ) {
        return `/my-profile/${loginUser.userid}/agent-reporting`;
      }

      // Default redirect based on business snapshot setting
      return process.env.VITE_APP_BUSINESS_SNAPSHOT === '1'
        ? '/call-reporting/business-snapshot'
        : '/call-reporting/agent-summary';
    },
    []
  );

  /**
   * Used for login and setting recoil global state (user)
   *
   * @param {Object} values Formik values from login page
   * @param {Function} setStatus Notify Login of status
   * @param {Function} setSubmitting
   * @param {*} intl Internationalization injected by injectIntl
   * @param {Function} resetInitialState Reset formik initial values (might not work)
   */
  const handleLogin = useCallback(
    async (credentials, setStatus, setSubmitting, intl, resetInitialState) => {
      // login to back-end
      const response = await loginAjax({
        credentials,
        setUserAtom,
      });

      const redirectUrl = sessionStorage?.getItem('redirect_url');
      const { loginUser, success } = response;

      // Perform redirect immediately after successful login to prevent 404 flash
      if (success && loginUser) {
        const targetPath = calculateRedirectPath(loginUser, redirectUrl);
        history.replace(targetPath);
        sessionStorage.removeItem('redirect_url');
        sessionStorage.removeItem('logout');
      }

      if (setSubmitting) {
        setSubmitting(false);
      }
      if (response.error?.data?.captchaRequired) {
        setStatus(
          intl.formatMessage({
            id: 'AUTH.VALIDATION.REQUIRES_CAPTCHA',
          })
        );
        resetInitialState();
      }
      if (!response.success && setStatus) {
        let errorId = 'AUTH.VALIDATION.INVALID_LOGIN';
        const errorType =
          response.error?.response?.data?.errorType || response.error;
        if (errorType && errorType === 'REQUIRES_PASSWORD_UPDATE') {
          errorId = 'AUTH.VALIDATION.REQUIRES_PASSWORD_UPDATE';

          const url = `${process.env.VITE_APP_API_DOMAIN}/itn/legacy/forgotPassword`;
          await Axios.post(url, {
            call: 'forgotPassword',
            email: credentials.email,
            react: true,
          });
        }

        setStatus(
          intl.formatMessage({
            id: errorId,
          })
        );
        resetInitialState();
      }

      if (success && loginUser) {
        setStatus(null);
        trackLogIn();

        await tryTrackTrialStatus(loginUser);

        // check for blacklisted word here
        const bannedWordFound = bannedPasswordParts.some((bannedWord) => {
          return credentials.password.toLowerCase().includes(bannedWord);
        });

        if (
          success &&
          loginUser &&
          bannedWordFound &&
          credentials.email.includes('kixie.com')
        ) {
          localStorage.clear(); //clear local storage
          mixpanelReset();
          logoutAjax({ setUserAtom }); // logout by pass no params
          localStorage.setItem('password_reset_required', `${Date.now()}`);

          const url = `${process.env.VITE_APP_API_DOMAIN}/itn/legacy/forgotPassword`;
          const result = await Axios.post(url, {
            call: 'forgotPassword',
            email: credentials.email,
            react: true,
          });
          if (result?.data?.success) {
            enqueueSnackbarWithStyles({
              message:
                'Your password does not meet current security standards. Check your email for a link to update your password.',
              persist: true,
              alertProps: { severity: 'success' },
            });

            history.push({
              pathname: '/login',
            });
          } else {
            enqueueSnackbarWithStyles({
              message:
                'There was a problem ressetting your password please contact kixie support.',
              alertProps: { severity: 'error' },
              persist: true,
            });
          }
        }

        const result = await mutation.mutateAsync({
          method: 'GET',
          domain: process.env.VITE_APP_CDK_DOMAIN,
          endpoint: endpointEnum.GET_USER_PRODUCTS.replace(
            '{businessId}',
            loginUser.business.businessid
          ).replace('{userId}', loginUser.userid),
          payload: data,
        });
        if (result.data) {
          localStorage.setItem('userProducts', JSON.stringify(result.data));
        }
      }
      return response;
    },
    [setUserAtom, calculateRedirectPath, history] // specify what parameters being used from closure (outer context)
  );
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  useEffect(() => {
    if (pathname !== '/login' && pathname !== '/change-password') {
      refreshTokenAjax({ setUserAtom });
      intervalRef.current && clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        const accessToken = cookie.load('accessToken');
        if (!accessToken) {
          refreshTokenAjax({ setUserAtom, suppressLogout: true });
        }
      }, 59_000); // check every 59 seconds since access tokens expires 1 min after 9 min
    }
    return () => {
      intervalRef.current && clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, setUserAtom]);

  useEffect(() => {
    // ENG-8377:whenever the recoilUserSync changes, update userAtom with new userSettings from decoded access token\
    if (recoilUserSync.payload) {
      setUserAtom({
        ...userAtom,
        ...recoilUserSync.payload,
      });
    }
  }, [recoilUserSync.payload?.apikey]);

  // update page after userAtom update done by login/refesh
  useEffect(() => {}, [userAtom]);

  async function emptyCache() {
    if ('caches' in window) {
      caches.keys().then((names) => {
        // Delete all the cache files
        names.forEach((name) => {
          caches.delete(name);
        });
      });

      // clear cache for build.js
      try {
        await Axios({
          url: window.location.origin + '/build.js',
          headers: {
            Pragma: 'no-cache',
            Expires: -1,
            'Cache-Control': 'no-cache',
          },
        });
      } catch (exception) {
        // no-op
      }

      // Makes sure the page reloads. Changes are only visible after you refresh.
      (window.location.reload as any)(true);
    }
  }
  const data = {
    // gitBranch:
    //   window.kixieBuildEnv?.gitBranch === 'Unknown'
    //     ? 'develop'
    //     : window.kixieBuildEnv?.gitBranch,
    // commitDate: window.kixieBuildEnv?.date,
    gitCommitHash: window.kixieBuildEnv.git,
    // gitCommitHash: '631b38f',
    url: window.location.origin,
    // url: 'https://app.kixie.com',
    extraneous: pathname,
  };

  const lastCheckedDatetime = localStorage.getItem('last_checked_datetime');
  const currentDatetime = Date.now();
  const checkUpdateTTL = 5 * 60 * 1000;
  const shouldCheckUpdate =
    !lastCheckedDatetime ||
    (lastCheckedDatetime &&
      currentDatetime - Number(lastCheckedDatetime) > checkUpdateTTL);

  const parameters = querystring.stringify(data);
  const checkUpdate = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.CHECK_APP_UPDATE + `?${parameters}`,
      method: 'get',
      waitForPayload: !shouldCheckUpdate,
    })
  );
  const checkUpdateMemo = useMemo(
    () => (checkUpdate.state === 'hasValue' ? checkUpdate.contents : {}),
    [pathname, checkUpdate]
  );

  shouldCheckUpdate &&
    checkUpdate.state === 'hasValue' &&
    localStorage.setItem('last_checked_datetime', currentDatetime.toString());

  useEffect(() => {
    let message;

    let appUpdateAvail = false;

    if (checkUpdateMemo?.response?.data) {
      const responseBody = checkUpdateMemo?.response?.data?.appUpdateAvailable;
      appUpdateAvail = responseBody;
    }
    if (!!checkUpdateMemo?.response && appUpdateAvail) {
      message = (
        <div>
          <p>
            A newer version of the dashboard is available.
            <br />
            Click Update Now to use the new version.
          </p>
          {userAtom?.user?.role === 'superadmin' ? (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <p>
                  {`Current Commit: ${
                    checkUpdateMemo?.response?.data?.gitCommitHash ?? ''
                  }`}
                  <br />
                  {`Latest Commit: ${
                    checkUpdateMemo?.response?.data?.gitCommitHashRemote ?? ''
                  }`}
                </p>
              </div>
              <Button
                style={{ height: '36px', marginLeft: '8px' }}
                // className={classes.button}
                variant="contained"
                onClick={() => {
                  emptyCache();
                }}
              >
                Update Now
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                // className={classes.button}
                variant="contained"
                onClick={() => {
                  emptyCache();
                }}
              >
                Update Now
              </Button>
            </div>
          )}
        </div>
      );
      enqueueSnackbarWithStyles(
        {
          animationDuration: 0,
          message,
          alertProps: { severity: 'success' },
          persist: true,
        },
        { preventDuplicate: true, key: 'updateAvailable' }
      );
    }
  }, [checkUpdateMemo, pathname]);
  if (
    !userFromLocalStorage &&
    pathname !== '/login' &&
    sessionStorage?.getItem('logout') !== 'true'
  ) {
    const redirectUrl = pathname + search;
    sessionStorage?.setItem('redirect_url', redirectUrl);
  }
  return (
    <Switch>
      <Route path="/error" component={ErrorsPage} />
      <Route path="/verifyEmail">
        <VerifyEmail />
      </Route>
      {userFromLocalStorage ? ( // if auth show layout
        <Switch>
          <Route
            exact
            path="/leaderboard"
            component={
              userAtom?.user.role === 'superadmin' ||
              userAtom?.user.role === 'admin' ||
              userAtom?.user.role === 'manager' ||
              permissions?.canViewCallReporting
                ? OldLeaderboard
                : UnauthorizedPage
            }
          />
          <Route
            exact
            path="/leaderboard/:id"
            component={
              userAtom?.user.role === 'superadmin' ||
              userAtom?.user.role === 'admin' ||
              userAtom?.user.role === 'manager' ||
              permissions?.canViewCallReporting
                ? Leaderboard
                : UnauthorizedPage
            }
          />
          <Route
            exact
            path="/aio/outcome"
            component={
              userAtom?.user.role === 'superadmin' ||
              userAtom?.user.role === 'admin' ||
              userAtom?.user.role === 'manager' ||
              userAtom?.user.role === 'user' ||
              permissions?.canViewCallReporting
                ? AIOOutcome
                : UnauthorizedPage
            }
          />
           <Route
            exact
            path="/insights"
            component={
              process.env.VITE_APP_OMNI_ANALYTICS === '1'
                ? OmniReporting
                : UnauthorizedPage
            }
          />
          <Layout classes={classes}>
            {userAtom && <BasePage userAtom={userAtom} />}
          </Layout>
        </Switch>
      ) : (
        /* Redirect to `/auth` when user is not authorized */
        <Switch>
          <Route
            path="/login"
            render={() => <Login handleLogin={handleLogin} />}
          />
          <Route
            path="/change-password"
            render={() => <ChangePasswordTyped handleLogin={handleLogin} />}
          />
          <Redirect to="/login" />
        </Switch>
      )}
    </Switch>
  );
}

export default withRouter<IRoutes, React.ComponentType<IRoutes>>(Routes);
